"use client"

import { useState, useEffect, useContext } from "react"
import {
  Box,
  TextField,
  Button,
  IconButton,
  Tooltip,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Autocomplete,
  Typography,
  Divider,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Grid,
  Fade,
  useTheme,
  alpha,
  Snackbar,
  Alert,
  Slide,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from "@mui/material"
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline"
import DeleteIcon from "@mui/icons-material/Delete"
import { ErpContext } from "../../context/ErpContext.js"
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt"
import ModalCrearEntidad from "../adminComponent/ModalCrearEntidad.js"
import InventoryIcon from "@mui/icons-material/Inventory"
import AttachMoneyIcon from "@mui/icons-material/AttachMoney"
import PriceCheckIcon from "@mui/icons-material/PriceCheck"
import ContentCopyIcon from "@mui/icons-material/ContentCopy"
import LocalOfferIcon from "@mui/icons-material/LocalOffer"
import DescriptionIcon from "@mui/icons-material/Description"
import PersonIcon from "@mui/icons-material/Person"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered"
import HailIcon from '@mui/icons-material/Hail'
import CloseIcon from "@mui/icons-material/Close"
import WarningAmberIcon from "@mui/icons-material/WarningAmber"


// Componente de transición para las alertas
function SlideTransition(props) {
  return <Slide {...props} direction="up" />
}

/**
 * 1) ENCABEZADO: se rellenan datos únicos del item:
 *    código, código de barras (scanner), descripción, y atributos.
 * 2) Al hacer clic en "Confirmar/Crear Ítem", se muestra el bloque con las 3 tablas.
 */
export default function ItemWithThreeTables({ onClose, selectedItem = null }) {
  const {
    getUbicacion,
    ubicaciones,
    getEntidadByTipoEntidad,
    entidadesTipoEntidad,
    getEntidadesClientesTresLetras,
    entidadesClientesTresLetras,
    itemAtributoNoEliminados,
    getItemAtributoNoEliminados,
    postItem,
    postItemUbicacion,
    postListaMonto,
    getUsuario,
    usuarios,
    getItemByCodigoItem,
    postEntidad,
    getEntidadByID,
    isNavbarCollapsed,
    getParametrosGlobales,
    // Funciones adicionales para edición
    updateItem,
    updateItemUbicaciones,
    getItemUbicacionFilter,
    itemUbicacionFilter,
    getListaMontosByIdItem,
    updateListaDeMonto,
  } = useContext(ErpContext)

  // Determinar si estamos en modo edición
  const isEditMode = selectedItem !== null

  const theme = useTheme()

  // -- Estados del encabezado
  const [codigoItem, setCodigoItem] = useState("")
  const [descripcionItem, setDescripcionItem] = useState("")
  const [pluBalanzaItem, setPluBalanzaItem] = useState("")
  const [searchTextCliente, setSearchTextCliente] = useState("") // Texto de búsqueda
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(null) // Usuario seleccionado
  const [openModal, setOpenModal] = useState(false)
  
  // Estado para marcar item como eliminado (solo en edición)
  const [itemEliminado, setItemEliminado] = useState(false)
  
  // Estado para loading de datos en edición
  const [isLoadingData, setIsLoadingData] = useState(false)

  // Atributos globales (hasta 10) que se completan una sola vez
  const [atributosForm, setAtributosForm] = useState([])

  // Manejo del "Item Creado" para mostrar las tablas
  const [isItemCreated, setIsItemCreated] = useState(false)

  // Estados para alertas y carga
  const [alertOpen, setAlertOpen] = useState(false)
  const [alertMessage, setAlertMessage] = useState("")
  const [alertSeverity, setAlertSeverity] = useState("success")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [entidadCreatedAlert, setEntidadCreatedAlert] = useState(false)
  const [entidadMessage, setEntidadMessage] = useState("")
  const [parametrosGlobales, setParametrosGlobales] = useState({})

  const showPluField = parametrosGlobales.pluBalanza === "1"
  const headerColumnSize = showPluField ? 4 : 6

  // Estado derivado para ubicaciones seleccionadas en la tabla de stock
  const [stockUbicaciones, setStockUbicaciones] = useState([])

  // -------------------------------------
  //  TABLA #1: STOCK
  // -------------------------------------
  const [stockRows, setStockRows] = useState([])

  // Efecto para actualizar las ubicaciones disponibles basadas en la tabla de stock
  useEffect(() => {
    // Extraer las ubicaciones seleccionadas de las filas de stock
    const ubicacionesSeleccionadas = stockRows
      .filter((row) => row.ubicacion) // Filtrar solo las filas con ubicación seleccionada
      .map((row) => row.ubicacion) // Obtener los IDs de ubicación
      .filter((id, index, self) => self.indexOf(id) === index) // Eliminar duplicados

    // Actualizar el estado de ubicaciones disponibles para las otras tablas
    setStockUbicaciones(ubicacionesSeleccionadas)
  }, [stockRows])

  // Efecto para cargar datos iniciales
  useEffect(() => {
    async function fetchData() {
      try {
        await getUbicacion()
        await getUsuario()
        await getEntidadByTipoEntidad(2)
        await getEntidadesClientesTresLetras("")
        await getItemAtributoNoEliminados()
        const parametros = await getParametrosGlobales(["pluBalanza"])
        if (parametros) {
          setParametrosGlobales(parametros)
        }
      } catch (error) {
        console.error("Error al cargar datos:", error)
        showAlert("Error al cargar datos iniciales", "error")
      }
    }
    fetchData()
  }, [])

  useEffect(() => {
    if (searchTextCliente.length >= 3) {
      getEntidadesClientesTresLetras(searchTextCliente, 1) // Llama a la API
    }
  }, [searchTextCliente]) //Este paramatro searchText condiciona al useEffect, detectando sus cambios. Solo se ejecuta si cambia el valor del input de searchText.

  // Al cambiar itemAtributoNoEliminados, inicializar atributosForm
  useEffect(() => {
    if (itemAtributoNoEliminados?.length > 0) {
      // Si estamos en modo edición, usar los valores del item seleccionado
      if (isEditMode && selectedItem?.activeAttributes) {
        const inicial = itemAtributoNoEliminados.map((attr) => ({
          id: attr.id,
          descripcion: attr.descripcion,
          valor: selectedItem.activeAttributes[`itemDatoAtributo${attr.id}`] || "",
        }))
        setAtributosForm(inicial)
      } else {
        // Modo creación: valores vacíos
        const inicial = itemAtributoNoEliminados.map((attr) => ({
          id: attr.id,
          descripcion: attr.descripcion,
          valor: "",
        }))
        setAtributosForm(inicial)
      }
    }
  }, [itemAtributoNoEliminados, isEditMode, selectedItem])

  // Efecto para cargar datos del item cuando estamos en modo edición
  useEffect(() => {
    const loadItemData = async () => {
      if (!isEditMode || !selectedItem) return
      
      setIsLoadingData(true)
      try {
        // Pre-popular datos básicos
        setCodigoItem(selectedItem.codigo || "")
        setDescripcionItem(selectedItem.descripcion || "")
        setPluBalanzaItem(selectedItem.pluBalanza || "")
        setItemEliminado(selectedItem.eliminado || false)
        
        // Cargar ubicaciones y precios existentes
        const ubicacionesItem = await getItemUbicacionFilter(selectedItem.idItem)
        const preciosItem = await getListaMontosByIdItem(selectedItem.idItem)
        
        // Filtrar precios con idEntidad = 1 (precio base)
        const preciosBase = preciosItem.filter(p => p.idEntidad === 1)
        
        // Construir stockRows con datos existentes
        if (ubicacionesItem && ubicacionesItem.length > 0) {
          const rows = ubicacionesItem.map((ub) => {
            // Buscar el precio correspondiente para esta ubicación
            const precioUbicacion = preciosBase.find(p => p.idUbicacion === ub.idUbicacion)
            return {
              id: ub.id || Date.now() + Math.random(),
              ubicacion: ub.idUbicacion,
              inventario: ub.inventario || 0,
              precio: precioUbicacion ? precioUbicacion.monto : "",
            }
          })
          setStockRows(rows)
        }
        
        // En modo edición, mostrar directamente las tablas
        setIsItemCreated(true)
        
      } catch (error) {
        console.error("Error al cargar datos del item:", error)
        showAlert("Error al cargar datos del grupo", "error")
      } finally {
        setIsLoadingData(false)
      }
    }
    
    loadItemData()
  }, [isEditMode, selectedItem])

  // Manejar cambio en atributos del encabezado
  const handleAtributoFormChange = (id, valor) => {
    setAtributosForm((prev) => prev.map((attr) => (attr.id === id ? { ...attr, valor } : attr)))
  }

  // Función para mostrar alertas
  const showAlert = (message, severity = "success") => {
    setAlertMessage(message)
    setAlertSeverity(severity)
    setAlertOpen(true)
  }

  // Función para confirmar la creación del Ítem
  // (ej: validas si se llenó código, descripción)
  const handleCrearItem = async () => {
    if (!codigoItem || !descripcionItem) {
      showAlert("Por favor completa Nombre y Descripción.", "warning")
      return
    }

    // Validar que el código tenga al menos 3 caracteres
    if (codigoItem.trim().length < 3) {
      showAlert("El código debe tener al menos 3 caracteres", "warning")
      return
    }

    try {
      const existeItem = await getItemByCodigoItem(codigoItem)
      if (existeItem) {
        showAlert("Ya existe un ítem con ese código", "error")
        return
      }

      // Se marca como creado para mostrar las 3 tablas
      setIsItemCreated(true)
      showAlert("Grupo confirmado. Complete las tablas para finalizar.", "info")
    } catch (error) {
      console.error("Error al verificar el código:", error)
      showAlert("Error al verificar el código del ítem", "error")
    }
  }

  // Función para resetear el formulario completo
  const resetForm = () => {
    // Resetear campos del encabezado
    setCodigoItem("")
    setDescripcionItem("")
    setPluBalanzaItem("")
    setUsuarioSeleccionado(null)

    // Resetear atributos
    if (itemAtributoNoEliminados?.length > 0) {
      const resetAtributos = itemAtributoNoEliminados.map((attr) => ({
        id: attr.id,
        descripcion: attr.descripcion,
        valor: "",
      }))
      setAtributosForm(resetAtributos)
    }

    // Resetear tablas
    setStockRows([])
    setPriceRows([])

    // Resetear estado de creación
    setIsItemCreated(false)
  }

  // Agregar fila
  const handleAddStockRow = () => {
    setStockRows((prev) => [
      ...prev,
      {
        id: Date.now(),
        ubicacion: null,
        inventario: "",
        precio: "",
      },
    ])
  }
  // Copiar fila
  const handleCopyStockRow = (rowId) => {
    setStockRows((prev) => {
      const index = prev.findIndex((r) => r.id === rowId)
      if (index === -1) return prev
      const original = prev[index]
      const copia = { ...original, id: Date.now() }
      const newRows = [...prev]
      newRows.splice(index + 1, 0, copia)
      return newRows
    })
  }
  // Eliminar fila
  const handleDeleteStockRow = (rowId) => {
    setStockRows((prev) => {
      const newRows = prev.filter((r) => r.id !== rowId)

      // Si se eliminó la última fila, limpiar las filas de precio
      if (newRows.length === 0) {
        setPriceRows([])
      }

      return newRows
    })
  }
  // Manejar cambios
  const handleChangeStockRow = (rowId, field, value) => {
    setStockRows((prev) => {
      const newRows = prev.map((row) =>
        row.id === rowId
          ? {
              ...row,
              [field]: value,
            }
          : row,
      )

      // Si se cambió una ubicación, verificar si hay que actualizar las filas de precio
      if (field === "ubicacion") {
        // Obtener todas las ubicaciones seleccionadas en la tabla de stock
        const ubicacionesSeleccionadas = newRows
          .filter((row) => row.ubicacion)
          .map((row) => row.ubicacion)
          .filter((id, index, self) => self.indexOf(id) === index)

        // Actualizar las filas de precio para eliminar ubicaciones que ya no están en stock
        setPriceRows((prev) =>
          prev.map((row) => {
            if (row.ubicacion && !ubicacionesSeleccionadas.includes(row.ubicacion)) {
              return { ...row, ubicacion: null }
            }
            return row
          }),
        )
      }

      return newRows
    })
  }

  // -------------------------------------
  //  TABLA #2: PRECIOS (Clientes)
  // -------------------------------------
  const [priceRows, setPriceRows] = useState([])
  // Agregar fila
  // Modificar la función handleAddPriceRow
  const handleAddPriceRow = async () => {
    // Solo permitir agregar filas si hay ubicaciones en stock
    if (stockRows.length === 0) {
      showAlert("Primero debe agregar ubicaciones en la tabla de Stock", "warning")
      return
    }

    // Obtener cliente genérico automáticamente
    let clienteGenerico = null;
    try {
      const defaultEntidad = await getEntidadByID(1);
      if (defaultEntidad) {
        clienteGenerico = {
          label: `${defaultEntidad.descripcion} - ${defaultEntidad.apellido} - ${defaultEntidad.dniCuitCuil}`,
          id: defaultEntidad.id,
        };
      }
    } catch (error) {
      console.error("Error al obtener cliente genérico:", error);
    }

    setPriceRows((prev) => [
      ...prev,
      {
        id: Date.now(),
        ubicacion: stockUbicaciones.length === 1 ? stockUbicaciones[0] : null, // Si solo hay una ubicación, seleccionarla automáticamente
        cliente: clienteGenerico ? clienteGenerico.id : null, // Cliente genérico por defecto (ID = 1)
        clienteData: clienteGenerico, // Datos completos del cliente para el Autocomplete
        precio: "",
      },
    ])
  }
  // Copiar fila
  const handleCopyPriceRow = async (rowId) => {
    setPriceRows((prev) => {
      const index = prev.findIndex((r) => r.id === rowId)
      if (index === -1) return prev
      const original = prev[index]
      
      // Si no hay cliente seleccionado, usar cliente genérico
      let clienteId = original.cliente;
      if (!clienteId) {
        clienteId = 1; // Cliente genérico por defecto
      }
      
      const copia = { 
        ...original, 
        id: Date.now(),
        cliente: clienteId
      }
      const newRows = [...prev]
      newRows.splice(index + 1, 0, copia)
      return newRows
    })
  }
  // Eliminar fila
  const handleDeletePriceRow = (rowId) => {
    setPriceRows((prev) => prev.filter((r) => r.id !== rowId))
  }
  // Manejar cambios
  const handleChangePriceRow = (rowId, field, value) => {
    setPriceRows((prev) => prev.map((row) => {
      if (row.id === rowId) {
        if (field === "cliente") {
          // Cuando se cambia el cliente, también actualizar clienteData
          const clienteEncontrado = entidadesClientesTresLetras.find((it) => it.id === value);
          return {
            ...row,
            [field]: value,
            clienteData: clienteEncontrado ? {
              label: `${clienteEncontrado.descripcion} - ${clienteEncontrado.apellido} - ${clienteEncontrado.dniCuitCuil}`,
              id: clienteEncontrado.id,
            } : null
          };
        }
        return { ...row, [field]: value };
      }
      return row;
    }))
  }

  const abrirModalCrearEntidad = () => {
    setOpenModal(true)
  }

  const handleCreateEntidad = async (nuevaEntidad) => {
    try {
      console.log("Entidad a crear:", nuevaEntidad)

      // POST: Crea la entidad y recibe el objeto con su ID
      const entidadCreada = await postEntidad(
        nuevaEntidad.descripcion,
        nuevaEntidad.apellido,
        nuevaEntidad.dniCuitCuil,
        nuevaEntidad.telefono,
        nuevaEntidad.email,
        nuevaEntidad.direccion,
        nuevaEntidad.localidad,
        nuevaEntidad.provincia,
        nuevaEntidad.tipoEntidad,
      )

      // Actualizamos la lista de opciones en los autocompletes
      await getEntidadByTipoEntidad(2)

      if (entidadCreada.tipoEntidad === 1) {
        // CLIENTE: Si la última fila de Precios no tiene cliente, la asignamos.
        setPriceRows((prev) => {
          if (prev.length > 0 && !prev[prev.length - 1].cliente) {
            const newRows = [...prev]
            const lastRowId = newRows[newRows.length - 1].id

            // Forzamos la actualización del Autocomplete de cliente
            handleChangePriceRow(lastRowId, "cliente", entidadCreada.id)

            newRows[newRows.length - 1] = {
              ...newRows[newRows.length - 1],
              cliente: entidadCreada.id,
            }
            return newRows
          } else {
            // Si todas las filas ya tienen cliente, agregamos una nueva fila con la entidad seleccionada
            return [
              ...prev,
              {
                id: Date.now(),
                ubicacion: null,
                cliente: entidadCreada.id,
                precio: "",
              },
            ]
          }
        })
        setEntidadMessage(`Cliente ${entidadCreada.descripcion} creado con éxito`)
      } else if (entidadCreada.tipoEntidad === 2) {
        setEntidadMessage(`Proveedor ${entidadCreada.descripcion} creado con éxito`)
      }

      // Mostrar alerta de éxito
      setEntidadCreatedAlert(true)
      setTimeout(() => setEntidadCreatedAlert(false), 4000)

      // Cerrar el modal
      setOpenModal(false)
    } catch (error) {
      console.error("Error al crear la entidad:", error)
      showAlert("Error al crear la entidad", "error")
    }
  }

  const handleItemSubmit = async (e) => {
    e.preventDefault()

    // Validaciones
    if (!codigoItem || !descripcionItem) {
      showAlert("Por favor complete la información básica del ítem", "warning")
      return
    }

    // Validar que el código tenga al menos 3 caracteres
    if (codigoItem.trim().length < 3) {
      showAlert("El código debe tener al menos 3 caracteres", "warning")
      return
    }

    if (stockRows.length === 0) {
      showAlert("Debe agregar al menos una fila de stock", "warning")
      return
    }

    // Validar que todas las filas tengan datos completos
    const stockIncompleto = stockRows.some((row) => !row.ubicacion)
    const preciosIncompleto = priceRows.some((row) => !row.ubicacion || !row.cliente || row.precio === "")

    if (stockIncompleto || preciosIncompleto) {
      showAlert("Hay filas con información incompleta en las tablas", "warning")
      return
    }

    setIsSubmitting(true)

    try {
      if (isEditMode) {
        // ========== MODO EDICIÓN ==========
        console.log("Actualizando item...", selectedItem.idItem)
        
        // 1. Actualizar datos básicos del item
        await updateItem(
          selectedItem.idItem,
          descripcionItem,
          codigoItem,
          null, // codigoScanner no aplica para items de grupo
          atributosForm,
          itemEliminado,
          showPluField ? pluBalanzaItem : null,
          null, // descripcionServicio no aplica para grupos
          false // esActividad no aplica para grupos
        )
        
        // 2. Actualizar ubicaciones (pasajeros/inventario)
        const ubicacionesPayload = stockRows.map((row) => ({
          idUbicacion: row.ubicacion,
          inventario: Number(row.inventario) || 0,
          stockMinimo: 0,
        }))
        await updateItemUbicaciones(selectedItem.idItem, ubicacionesPayload)
        
        // 3. Actualizar precios (idEntidad = 1)
        for (const row of stockRows) {
          if (row.precio !== "" && row.precio !== null && row.precio !== undefined) {
            const precioNum = Number.parseFloat(row.precio)
            if (!isNaN(precioNum) && precioNum >= 0) {
              await updateListaDeMonto({
                idItem: selectedItem.idItem,
                idUbicacion: Number.parseInt(row.ubicacion, 10),
                idEntidad: 1, // Cliente genérico
                monto: precioNum
              })
            }
          }
        }
        
        // Mostrar alerta de éxito
        showAlert(`Grupo ${codigoItem} actualizado exitosamente`, "success")
        
        // Cerrar modal si existe la función onClose
        if (onClose) {
          setTimeout(() => onClose(), 1500)
        }
        
      } else {
        // ========== MODO CREACIÓN ==========
        console.log("Creando item...")
        const response = await postItem(
          descripcionItem,
          codigoItem,
          atributosForm,
          null, // codigoScanner no aplica para items de grupo
          showPluField ? pluBalanzaItem : null,
          false // esServicio: false para items de grupo
        )
        // Obtener el último item creado
        const newItemId = response.data.id
        console.log("Nuevo item creado con ID: " + newItemId)

        // Crear ubicaciones de stock y precios
        for (const itemUbicacion of stockRows) {
          // Conversión a número
          const inventarioNum = Number(itemUbicacion.inventario);
          console.log("Creando ubicación de stock:", itemUbicacion)
          await postItemUbicacion(newItemId, itemUbicacion.ubicacion, inventarioNum, 0)
          
          // Si tiene precio, guardar en ListaDeMontos con idEntidad = 1 (cliente genérico)
          if (itemUbicacion.precio && itemUbicacion.precio !== "") {
            const precioNum = Number.parseFloat(itemUbicacion.precio);
            if (!isNaN(precioNum) && precioNum > 0) {
              console.log("Creando precio del grupo:", { idItem: newItemId, ubicacion: itemUbicacion.ubicacion, idEntidad: 1, precio: precioNum })
              await postListaMonto(
                newItemId,
                Number.parseInt(itemUbicacion.ubicacion, 10), // idUbicacion
                1, // idEntidad = 1 (cliente genérico - mismo que usa el paquete)
                precioNum
              )
            }
          }
        }

        // Crear precios adicionales (tabla de precios por cliente - si existe)
        for (const precioLista of priceRows) {
          await postListaMonto(
            newItemId,
            Number.parseInt(precioLista.ubicacion, 10), // Convierte a entero
            Number.parseInt(precioLista.cliente, 10), // Convierte a entero
            Number.parseFloat(precioLista.precio), // Convierte a float
          )
        }

        // Mostrar alerta de éxito
        showAlert(`Ítem ${codigoItem} creado exitosamente`, "success")

        // Resetear el formulario
        resetForm()
      }
    } catch (error) {
      console.error("Error en la creación/actualización del item:", error)
      showAlert(isEditMode ? "Error al actualizar el grupo" : "Error al crear el ítem", "error")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Box sx={{ p: 3, backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
      {/* ========== ENCABEZADO ========== */}
      <Card
        elevation={3}
        sx={{
          mb: 3,
          borderRadius: 2,
          overflow: "visible",
          position: "relative",
        }}
      >
        <CardHeader
          title={
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'rgb(244, 147, 14)' }}>
                {isEditMode ? `Editar Grupo: ${selectedItem?.descripcion || selectedItem?.codigo}` : "Crear Nuevo Grupo"}
              </Typography>
              {isEditMode && onClose && (
                <IconButton onClick={onClose} size="small" sx={{ ml: 2 }}>
                  <CloseIcon />
                </IconButton>
              )}
            </Box>
          }
          subheader=""
          sx={{
            backgroundColor: alpha(theme.palette.primary.main, 0.05),
            borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        />

        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={3}>
            {/* Primera fila */}
            <Grid item xs={12} md={headerColumnSize}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <LocalOfferIcon sx={{ color: 'rgb(244, 147, 14)', mr: 1, my: 0.5 }} />
                <TextField
                  fullWidth
                  label="Nombre"
                  variant="outlined"
                  size="small"
                  value={codigoItem}
                  onChange={(e) => setCodigoItem(e.target.value)}
                  error={codigoItem.trim().length > 0 && codigoItem.trim().length < 3}
                  helperText={codigoItem.trim().length > 0 && codigoItem.trim().length < 3 ? "El código debe tener al menos 3 caracteres" : ""}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={headerColumnSize}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <DescriptionIcon sx={{ color: 'rgb(244, 147, 14)', mr: 1, my: 0.5 }} />
                <TextField
                  fullWidth
                  label="Descripción Grupo"
                  variant="outlined"
                  size="small"
                  value={descripcionItem}
                  onChange={(e) => setDescripcionItem(e.target.value)}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Box>
            </Grid>

            {/* {showPluField && (
              <Grid item xs={12} md={headerColumnSize}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <FormatListNumberedIcon sx={{ color: 'rgb(244, 147, 14)', mr: 1, my: 0.5 }} />
                  <TextField
                    fullWidth
                    label="PLU Balanza"
                    variant="outlined"
                    size="small"
                    value={pluBalanzaItem}
                    onChange={(e) => setPluBalanzaItem(e.target.value)}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Box>
              </Grid>
            )} */}

            {/* <Grid item xs={12} md={2}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <PersonIcon sx={{ color: 'rgb(244, 147, 14)', mr: 1, my: 0.5 }} />
                <Autocomplete
                  fullWidth
                  disablePortal
                  size="small"
                  options={usuarios.map((it) => ({
                    label: `${it.usuario} - ${it.nombre} - ${it.apellido}`,
                    nombre: it.nombre,
                    apellido: it.apellido,
                    id: it.id,
                    usuario: it.usuario,
                  }))}
                  value={usuarioSeleccionado}
                  onChange={(event, newValue) => {
                    setUsuarioSeleccionado(newValue)
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Usuario" sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
                  )}
                />
              </Box>
            </Grid> */}

            {/* Atributos */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }}>
                <Chip label="Atributos del Grupo" color="primary" variant="outlined" sx={{ fontWeight: 500, color: '#5b5b5f',
                border: '1px solid #5b5b5f', }} />
              </Divider>

              <Grid container spacing={2} sx={{ mt: 1 }}>
                {atributosForm.map((attr) => (
                  <Grid item xs={12} sm={6} md={3} lg={2} key={attr.id}>
                    <TextField
                      fullWidth
                      label={attr.descripcion}
                      value={attr.valor}
                      onChange={(e) => handleAtributoFormChange(attr.id, e.target.value)}
                      variant="outlined"
                      size="small"
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>

          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
            {/* <Tooltip title="Crear proveedor/cliente">
              <Button
                variant="outlined"
                startIcon={<PersonAddAltIcon />}
                onClick={abrirModalCrearEntidad}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  px: 3,
                }}
              >
                Crear Entidad
              </Button>
            </Tooltip> */}

            <Button
              variant="contained"
              size="large"
              onClick={handleCrearItem}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                px: 4,
                boxShadow: 3,
                backgroundColor: '#5b5b5f', color: 'white',
                color: 'white',
              }}
            >
              Confirmar Grupo
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* ========== Sección de Tablas (solo si item creado) ========== */}
      {isItemCreated && (
        <Fade in={isItemCreated} timeout={500}>
          <Box>
            <Grid container spacing={3}>
              {/* ========== TABLA 1: STOCK ========== */}
              <Grid item xs={12}>
                <Card elevation={3} sx={{ borderRadius: 2, height: "100%" }}>
                  <CardHeader
                    avatar={<HailIcon sx={{color:'rgb(244, 147, 14)'}} />}
                    title={
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Destinos
                        </Typography>
                        <Tooltip title="Agregar fila">
                          <IconButton onClick={handleAddStockRow} size="small" sx={{ ml: 1 }}>
                            <AddCircleOutlineIcon sx={{color:'rgb(244, 147, 14)'}} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    }
                    // subheader={
                    //   <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                    //     Agregue destinos para el Grupo
                    //   </Typography>
                    // }
                    sx={{
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                      borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    }}
                  />

                  <CardContent sx={{ p: 0, height: "calc(100% - 72px)" }}>
                    <TableContainer
                      sx={{
                        height: "100%",
                        overflowY: "auto",
                        display: "block",
                      }}
                    >
                      <Table stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell
                              align="center"
                              sx={{
                                fontWeight: "bold",
                                backgroundColor: '#5b5b5f', color: 'white',
                                width: "35%",
                              }}
                            >
                              Destino
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                fontWeight: "bold",
                                backgroundColor: '#5b5b5f', color: 'white',
                                width: "20%",
                              }}
                            >
                              Pasajeros
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                fontWeight: "bold",
                                backgroundColor: '#5b5b5f', color: 'white',
                                width: "25%",
                              }}
                            >
                              Precio
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                fontWeight: "bold",
                                backgroundColor: '#5b5b5f', color: 'white',
                                width: "20%",
                              }}
                            >
                              Acciones
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {stockRows.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                                <Typography color="text.secondary">
                                  No hay registros. Haga clic en + para agregar.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            stockRows.map((row) => (
                              <TableRow key={row.id} hover>
                                {/* Ubicación */}
                                <TableCell>
                                  <Autocomplete
                                    size="small"
                                    options={ubicaciones || []}
                                    getOptionLabel={(opt) => opt.descripcion}
                                    value={ubicaciones?.find((u) => u.id === row.ubicacion) || null}
                                    onChange={(e, newValue) =>
                                      handleChangeStockRow(row.id, "ubicacion", newValue ? newValue.id : null)
                                    }
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        size="small"
                                        label="Destino"
                                        variant="outlined"
                                        sx={{
                                          "& .MuiOutlinedInput-root": {
                                            borderRadius: 1.5,
                                          },
                                        }}
                                      />
                                    )}
                                    ListboxProps={{
                                      style: {
                                        maxHeight: 200,
                                      },
                                    }}
                                  />
                                </TableCell>
                                   {/* Inventario */}
                                <TableCell>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label="Pasajeros"
                                    value={row.inventario}
                                    onChange={(e) => handleChangeStockRow(row.id, "inventario", e.target.value)}
                                    variant="outlined"
                                    sx={{
                                      "& .MuiOutlinedInput-root": {
                                        borderRadius: 1.5,
                                      },
                                    }}
                                  />
                                </TableCell>
                                {/* Precio */}
                                <TableCell>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label="Precio"
                                    value={row.precio}
                                    onChange={(e) => handleChangeStockRow(row.id, "precio", e.target.value)}
                                    variant="outlined"
                                    InputProps={{
                                      startAdornment: <span style={{ marginRight: 4 }}>$</span>,
                                    }}
                                    sx={{
                                      "& .MuiOutlinedInput-root": {
                                        borderRadius: 1.5,
                                      },
                                    }}
                                  />
                                </TableCell>
                                {/* Acciones */}
                                <TableCell align="center">
                                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                                    <Tooltip title="Copiar Fila">
                                      <IconButton onClick={() => handleCopyStockRow(row.id)} color="info" size="small">
                                        <ContentCopyIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Eliminar Fila">
                                      <IconButton
                                        onClick={() => handleDeleteStockRow(row.id)}
                                        color="error"
                                        size="small"
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
              {false && (
              <Grid item xs={12} md={7}>
                <Grid container spacing={3}>
                  {/* ========== TABLA 2: PRECIOS ========== */}
                  <Grid item xs={12}>
                    <Card
                      elevation={3}
                      sx={{
                        borderRadius: 2,
                        opacity: stockRows.length === 0 ? 0.7 : 1,
                        position: "relative",
                      }}
                    >
                      {stockRows.length === 0 && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 10,
                            backgroundColor: "rgba(255,255,255,0.7)",
                            borderRadius: 2,
                          }}
                        >
                          <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Agregar Destinos
                          </Typography>
                          
                        </Box>
                      )}
                      <CardHeader
                        avatar={<AttachMoneyIcon color={stockRows.length === 0 ? "disabled" : 'rgb(244, 147, 14)'} sx={{color:'rgb(244, 147, 14)'}} />}
                        title={
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: 600, color: stockRows.length === 0 ? "text.disabled" : "inherit" }}
                            >
                              Precios del Grupo
                            </Typography>
                            
                            <Tooltip title="Agregar fila">
                              <span>
                                <IconButton
                                  onClick={handleAddPriceRow}
                                  color="primary"
                                  size="small"
                                  sx={{ ml: 1, color:'rgb(244, 147, 14)' }}
                                  disabled={stockRows.length === 0}
                                >
                                  <AddCircleOutlineIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        }
                        subheader={
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                            Precios pare el grupo
                          </Typography>
                        }
                        sx={{
                          backgroundColor: alpha(theme.palette.primary.main, stockRows.length === 0 ? 0.02 : 0.05),
                          borderBottom: `1px solid ${alpha(theme.palette.primary.main, stockRows.length === 0 ? 0.05 : 0.1)}`,
                        }}
                      />

                      <CardContent sx={{ p: 0 }}>
                        <TableContainer sx={{ maxHeight: 250, overflowY: "auto" }}>
                          <Table stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell
                                  align="center"
                                  sx={{
                                    fontWeight: "bold",
                                    backgroundColor: '#5b5b5f', color: 'white',
                                    width: "35%",
                                  }}
                                >
                                  Destino
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    fontWeight: "bold",
                                    backgroundColor: '#5b5b5f', color: 'white',
                                    width: "35%",
                                  }}
                                >
                                  Cliente
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    fontWeight: "bold",
                                    backgroundColor: '#5b5b5f', color: 'white',
                                    width: "15%",
                                  }}
                                >
                                  Precio
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    fontWeight: "bold",
                                    backgroundColor: '#5b5b5f', color: 'white',
                                    width: "15%",
                                  }}
                                >
                                  Acciones
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {priceRows.length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                                    <Typography color="text.secondary">
                                      {stockRows.length === 0
                                        ? "Agregar Destinos"
                                        : "No hay registros. Haga clic en + para agregar."}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ) : (
                                priceRows.map((row) => (
                                  <TableRow key={row.id} hover>
                                    {/* Ubicación */}
                                    <TableCell>
                                      <Autocomplete
                                        size="small"
                                        options={ubicaciones?.filter((u) => stockUbicaciones.includes(u.id)) || []}
                                        getOptionLabel={(opt) => opt.descripcion}
                                        value={ubicaciones?.find((u) => u.id === row.ubicacion) || null}
                                        onChange={(e, newValue) =>
                                          handleChangePriceRow(row.id, "ubicacion", newValue ? newValue.id : null)
                                        }
                                        renderInput={(params) => (
                                          <TextField
                                            {...params}
                                            size="small"
                                            label="Destino"
                                            variant="outlined"
                                            sx={{
                                              "& .MuiOutlinedInput-root": {
                                                borderRadius: 1.5,
                                              },
                                            }}
                                          />
                                        )}
                                        ListboxProps={{
                                          style: {
                                            maxHeight: 200,
                                          },
                                        }}
                                        disabled={stockRows.length === 0}
                                      />
                                    </TableCell>
                                    {/* Cliente */}
                                    <TableCell>
                                      <Autocomplete
                                        size="small"
                                        disablePortal
                                        options={entidadesClientesTresLetras.map((it) => ({
                                          label: it.descripcion + " - " + it.apellido + " - " + it.dniCuitCuil,
                                          id: it.id,
                                        }))}
                                        value={
                                          row.clienteData || 
                                          entidadesClientesTresLetras.find((it) => it.id === row.cliente) ? {
                                            label: row.clienteData ? row.clienteData.label : 
                                              `${entidadesClientesTresLetras.find((it) => it.id === row.cliente)?.descripcion} - ${entidadesClientesTresLetras.find((it) => it.id === row.cliente)?.apellido} - ${entidadesClientesTresLetras.find((it) => it.id === row.cliente)?.dniCuitCuil}`,
                                            id: row.cliente
                                          } : null
                                        }
                                        onChange={(e, newValue) =>
                                          handleChangePriceRow(row.id, "cliente", newValue ? newValue.id : null)
                                        }
                                        onInputChange={(event, newInputValue) => setSearchTextCliente(newInputValue)}
                                        renderInput={(params) => (
                                          <TextField
                                            {...params}
                                            size="small"
                                            label="Cliente"
                                            variant="outlined"
                                            sx={{
                                              "& .MuiOutlinedInput-root": {
                                                borderRadius: 1.5,
                                              },
                                            }}
                                          />
                                        )}
                                        ListboxProps={{
                                          style: {
                                            maxHeight: 200,
                                          },
                                        }}
                                        disabled={stockRows.length === 0}
                                      />
                                    </TableCell>
                                    {/* Precio */}
                                    <TableCell>
                                      <TextField
                                        fullWidth
                                        size="small"
                                        type="number"
                                        label="Precio"
                                        value={row.precio}
                                        onChange={(e) => handleChangePriceRow(row.id, "precio", e.target.value)}
                                        InputProps={{
                                          startAdornment: (
                                            <Box component="span" sx={{ color: "text.secondary", mr: 0.5 }}>
                                              $
                                            </Box>
                                          ),
                                        }}
                                        variant="outlined"
                                        sx={{
                                          "& .MuiOutlinedInput-root": {
                                            borderRadius: 1.5,
                                          },
                                        }}
                                        disabled={stockRows.length === 0}
                                      />
                                    </TableCell>
                                    {/* Acciones */}
                                    <TableCell align="center">
                                      <Box sx={{ display: "flex", justifyContent: "center" }}>
                                        <Tooltip title="Copiar Fila">
                                          <IconButton
                                            onClick={() => handleCopyPriceRow(row.id)}
                                            color="info"
                                            size="small"
                                            disabled={stockRows.length === 0}
                                          >
                                            <ContentCopyIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Eliminar Fila">
                                          <IconButton
                                            onClick={() => handleDeletePriceRow(row.id)}
                                            color="error"
                                            size="small"
                                            disabled={stockRows.length === 0}
                                          >
                                            <DeleteIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                ))
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>                
              </Grid>
              )}

              {/* Checkbox para marcar como eliminado (solo en modo edición) */}
              {isEditMode && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      px: 3,
                      py: 2,
                      backgroundColor: itemEliminado ? alpha(theme.palette.warning.main, 0.1) : "transparent",
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={itemEliminado}
                          onChange={(e) => setItemEliminado(e.target.checked)}
                          color="warning"
                          icon={<WarningAmberIcon />}
                          checkedIcon={<WarningAmberIcon />}
                        />
                      }
                      label={
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 500,
                            color: itemEliminado ? theme.palette.warning.main : theme.palette.text.primary,
                          }}
                        >
                          Marcar como eliminado
                        </Typography>
                      }
                    />
                  </Box>
                </Grid>
              )}

              {/* Botón para guardar todo */}
              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                  <Button
                    variant="contained"
                    color="success"
                    size="large"
                    onClick={handleItemSubmit}
                    startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <InventoryIcon />}
                    disabled={isSubmitting}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      px: 5,
                      py: 1.5,
                      boxShadow: 3,
                    }}
                  >
                    {isSubmitting ? "Procesando..." : (isEditMode ? "Guardar Cambios" : "Crear Grupo Completo")}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Fade>
      )}

      {/* Modal para crear entidad */}
      <ModalCrearEntidad
        open={openModal}
        handleClose={() => setOpenModal(false)}
        onCreateEntidad={handleCreateEntidad}
      />

      {/* Alerta principal */}
      <Snackbar
        open={alertOpen}
        autoHideDuration={5000}
        onClose={() => setAlertOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        TransitionComponent={SlideTransition}
      >
        <Alert
          onClose={() => setAlertOpen(false)}
          severity={alertSeverity}
          variant="filled"
          sx={{
            width: "100%",
            boxShadow: 3,
            "& .MuiAlert-icon": {
              fontSize: "1.5rem",
            },
          }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>

      {/* Alerta de entidad creada */}
      <Snackbar
        open={entidadCreatedAlert}
        autoHideDuration={4000}
        onClose={() => setEntidadCreatedAlert(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        TransitionComponent={SlideTransition}
      >
        <Alert
          icon={<CheckCircleIcon fontSize="inherit" />}
          severity="success"
          variant="filled"
          sx={{
            width: "100%",
            boxShadow: 3,
            backgroundColor: theme.palette.success.main,
            "& .MuiAlert-icon": {
              fontSize: "1.5rem",
            },
          }}
        >
          {entidadMessage}
        </Alert>
      </Snackbar>
    </Box>
  )
}

